import { CentralAdminMachine, centralAdminMachineSchema } from "Shared/Components/Scope/Topology/CentralAdminTopology";
import { MaxSearchResultCount, universalSearchResultItem, useUniversalSearch } from "Shared/UniversalSearch";

import { AppRegistry } from "Apps/AppRegistry";
import { EnvironmentDefault } from "Shared/Components/Scope/Hierarchy/SubstrateScopeNodeDefaults";
import { ScopeItem } from "Shared/Components/Scope/Scopes.types";
import { convertScopeItemsToString } from "Shared/Components/Scope/Hooks/ScopeFilterHelperFunctions";
import { useCallback } from "react";
import { useEnvironment } from "Shared/Hooks";
import { useNavigate } from "react-router";
import { useORPCClient } from "Core/oRPC";
import { z } from "zod";

export const useServerUniversalSearch = () => {
    const navigate = useNavigate();
    const { inventoryApi } = useORPCClient();
    const { currentEnvironment } = useEnvironment();

    const searchServer = useCallback(
        async (searchText: string) => {
            if (!searchText) {
                return [];
            }
            searchText = searchText + "*";

            const resultList: universalSearchResultItem[] = [];

            try {
                const request = {
                    Environment: EnvironmentDefault.get(currentEnvironment) ?? "Prod",
                    SearchText: searchText,
                    Workload: "Exchange",
                    ResultsSize: MaxSearchResultCount,
                    Region: "",
                    MachineType: "All",
                    Forest: "",
                    Dag: ""
                };

                const response = await inventoryApi.postSearch(request);

                const parseResult = z.array(centralAdminMachineSchema).safeParse(response);
                const machines: CentralAdminMachine[] = parseResult.success ? parseResult.data : [];

                machines.forEach((machine) => {
                    if (machine.Name) {
                        const scopeItem: ScopeItem[] = [
                            {
                                ScopeType: "Environment",
                                Value: EnvironmentDefault.get(currentEnvironment) ?? "Prod"
                            },
                            {
                                ScopeType: "Ring",
                                Value: machine.DeploymentRing ?? "*"
                            },
                            {
                                ScopeType: "Region",
                                Value: machine.Region ?? ""
                            },
                            {
                                ScopeType: "Forest",
                                Value: machine.Forest ?? "",
                                ExtraProperties: machine.ForestAlias ? { Alias: machine.ForestAlias } : undefined
                            },
                            {
                                ScopeType: "Dag",
                                Value: machine.Dag ?? ""
                            },
                            {
                                ScopeType: "Machine",
                                Value: machine.Name ?? ""
                            }
                        ];

                        const queryString = convertScopeItemsToString(scopeItem, "SubstrateLogicalScopeHierarchy");
                        const healthPath = `details/machinemetrics?Workload=${request.Workload}&Scope=${queryString}`;
                        resultList.push({
                            id: machine.Name,
                            title: machine.Name,
                            description: machine.ProvisioningState ?? "",
                            onClick: () => navigate(`/server/${healthPath}`),
                            navigationTarget: `/server/${healthPath}`
                        });
                    }
                });
            } catch (error) {
                return resultList;
            }

            return resultList;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [navigate, currentEnvironment]
    );

    const { searchWord } = useUniversalSearch("Servers", "machine names", searchServer, false, AppRegistry.Server.path);
    return { searchWord };
};
